import type React from "react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Clock, GitCompareArrows, Info, LayoutGrid, List, Plus, Search, Tags, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { useClients, useDeleteClient, useRecentlyViewedClients } from "./useClients";
import { useAuth } from "@/features/auth/useAuth";
import { CreateClientDialog } from "./CreateClientDialog";
import { ClientInfoDialog } from "./ClientInfoDialog";
import { IndustriesDialog } from "./IndustriesDialog";
import { ComparePickerDialog } from "@/features/compare/ComparePickerDialog";
import { AlertDialog } from "@/components/ui/alert-dialog";
import { bandFor, BAND_META } from "@/features/overview/health";
import { isStale, daysSinceLatestPeriod } from "@/lib/format";
import { cn } from "@/lib/utils";
import { industryColor } from "@/features/insights/industry-colors";
import { pillTint, readablePillText } from "@/lib/colors";
import { useTheme } from "@/lib/theme";
import { client as clientSchemas, type client } from "@duress/shared";

type ViewMode = "card" | "list";
type PortfolioFilter = "all" | "mine";
type RegionFilter = "all" | client.Region;
type DialogKind = "info" | "industries" | "compare" | "delete" | null;

const PAGE_SIZE_CARD = 24;
const PAGE_SIZE_LIST = 30;

function parsePage(raw: string | null): number {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function filterClients(clients: client.ClientListItem[], query: string): client.ClientListItem[] {
  const term = query.trim().toLowerCase();
  if (!term) return clients;
  return clients.filter(
    (c) =>
      c.name.toLowerCase().includes(term) ||
      c.industries.some((i) => i.toLowerCase().includes(term)),
  );
}

const RECENTLY_VIEWED_LIMIT = 5;

export function ClientsPage(): React.JSX.Element {
  const { data: allClients, isLoading: clientsLoading, error, refetch } = useClients();
  const { data: recentlyViewed, isLoading: recentLoading } = useRecentlyViewedClients();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  // Search, view mode, portfolio filter and page all live in the URL rather
  // than component state, so the list you're looking at survives a refresh
  // and can be shared as a link. Each setter below drops the `page` param
  // when it changes something that would invalidate the current page.
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("q") ?? "";
  const setSearch = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("q", value);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const view: ViewMode = searchParams.get("view") === "list" ? "list" : "card";
  const setView = (value: ViewMode): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "list") next.set("view", value);
        else next.delete("view");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const portfolioFilter: PortfolioFilter = searchParams.get("portfolio") === "mine" ? "mine" : "all";
  const setPortfolioFilter = (value: PortfolioFilter): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "mine") next.set("portfolio", value);
        else next.delete("portfolio");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const regionParam = searchParams.get("region");
  const regionFilter: RegionFilter = clientSchemas.REGIONS.includes(regionParam as client.Region)
    ? (regionParam as client.Region)
    : "all";
  const setRegionFilter = (value: RegionFilter): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") next.delete("region");
        else next.set("region", value);
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const page = parsePage(searchParams.get("page"));
  const setPage = (updater: (prev: number) => number): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const nextPage = updater(parsePage(prev.get("page")));
        if (nextPage > 0) next.set("page", String(nextPage));
        else next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  // The four per-client dialogs (info/industries/compare/delete) are hoisted
  // here, one set for the whole page, instead of one set per card/row -- a
  // 30-row list used to mount 4 dialogs per row (120 total) even though at
  // most one is ever open at a time (UI-15).
  const [selectedClient, setSelectedClient] = useState<client.ClientListItem | null>(null);
  const [openDialog, setOpenDialog] = useState<DialogKind>(null);
  const del = useDeleteClient();
  const canDelete = user?.isAdmin ?? false;

  const openClientDialog = (c: client.ClientListItem, dialog: DialogKind): void => {
    setSelectedClient(c);
    setOpenDialog(dialog);
  };
  const closeDialog = (next: boolean): void => {
    if (!next) setOpenDialog(null);
  };
  const handleConfirmDelete = async (): Promise<void> => {
    if (!selectedClient) return;
    await del.mutateAsync(selectedClient.id);
    setOpenDialog(null);
  };

  const isSearching = search.trim().length > 0;
  const total = allClients?.length ?? 0;
  const isLoading = clientsLoading;
  const pageSize = view === "card" ? PAGE_SIZE_CARD : PAGE_SIZE_LIST;

  // Apply My Portfolio and Region filters, in that order, before search.
  const scopedBase: client.ClientListItem[] = (() => {
    let base = allClients ?? [];
    if (portfolioFilter === "mine" && user?.internalUserId) {
      base = base.filter((c) => c.csmUserId === user.internalUserId);
    }
    if (regionFilter !== "all") {
      base = base.filter((c) => c.region === regionFilter);
    }
    return base;
  })();
  const isScoped = portfolioFilter === "mine" || regionFilter !== "all";

  const searchResults = filterClients(scopedBase, search);
  const paginatedBase = isSearching ? searchResults : scopedBase;
  const showRecentlyViewed = !isSearching && !recentLoading && (recentlyViewed?.length ?? 0) > 0;
  const needsPagination = paginatedBase.length > pageSize;
  const pageCount = Math.ceil(paginatedBase.length / pageSize);
  const displayed = needsPagination
    ? paginatedBase.slice(page * pageSize, (page + 1) * pageSize)
    : paginatedBase;

  const subtitle = (() => {
    if (!allClients) return "—";
    if (isScoped) {
      const scopeLabel = [
        portfolioFilter === "mine" ? "your portfolio" : null,
        regionFilter !== "all" ? regionFilter : null,
      ]
        .filter(Boolean)
        .join(", ");
      const scoped = scopedBase.length;
      return isSearching
        ? `${searchResults.length} of ${scoped} in ${scopeLabel}`
        : `${scoped} ${scoped === 1 ? "client" : "clients"} in ${scopeLabel}`;
    }
    if (isSearching) return `${searchResults.length} of ${total} ${total === 1 ? "client" : "clients"}`;
    return `${total} ${total === 1 ? "client" : "clients"}`;
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Clients</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-full bg-black text-white hover:bg-black/85"
        >
          <Plus className="h-4 w-4" />
          New client
        </Button>
      </div>

      {!isLoading && allClients && allClients.length > 0 ? (
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search by name or industry…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {user?.internalUserId ? (
            <div className="flex items-center rounded-md border bg-background">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPortfolioFilter("all")}
                className={portfolioFilter === "all" ? "text-foreground" : "text-muted-foreground"}
              >
                All clients
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPortfolioFilter("mine")}
                className={portfolioFilter === "mine" ? "text-foreground" : "text-muted-foreground"}
              >
                My portfolio
              </Button>
            </div>
          ) : null}
          <div className="flex items-center rounded-md border bg-background">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRegionFilter("all")}
              className={regionFilter === "all" ? "text-foreground" : "text-muted-foreground"}
            >
              All regions
            </Button>
            {clientSchemas.REGIONS.map((r) => (
              <Button
                key={r}
                variant="ghost"
                size="sm"
                onClick={() => setRegionFilter(r)}
                className={regionFilter === r ? "text-foreground" : "text-muted-foreground"}
              >
                {r}
              </Button>
            ))}
          </div>
          <div className="flex items-center rounded-md border bg-background">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setView("card")}
              aria-label="Card view"
              className={view === "card" ? "text-foreground" : "text-muted-foreground"}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setView("list")}
              aria-label="List view"
              className={view === "list" ? "text-foreground" : "text-muted-foreground"}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}

      {showRecentlyViewed ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            Recently viewed
          </span>
          {recentlyViewed?.slice(0, RECENTLY_VIEWED_LIMIT).map((c) => (
            <Link
              key={c.id}
              to={`/clients/${c.slug}`}
              className="rounded-full border bg-background px-3 py-1 text-xs font-medium hover:bg-muted/60 transition-colors"
            >
              {c.name}
            </Link>
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <LoadingSkeleton variant="table" />
      ) : error ? (
        <ErrorCard message="Couldn't load clients." onRetry={refetch} />
      ) : !allClients || allClients.length === 0 ? (
        <NoClientsEmptyState onCreate={() => setCreateOpen(true)} />
      ) : isScoped && scopedBase.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {portfolioFilter === "mine" && regionFilter !== "all"
            ? `No ${regionFilter} clients are assigned to you yet.`
            : portfolioFilter === "mine"
              ? "No clients are assigned to you yet."
              : `No ${regionFilter} clients yet.`}
        </p>
      ) : isSearching && searchResults.length === 0 ? (
        <p className="text-sm text-muted-foreground">No clients match "{search}".</p>
      ) : view === "card" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {displayed.map((c) => (
            <ClientCard
              key={c.id}
              client={c}
              canDelete={canDelete}
              isDeleting={del.isPending}
              onEditInfo={() => openClientDialog(c, "info")}
              onEditIndustries={() => openClientDialog(c, "industries")}
              onCompare={() => openClientDialog(c, "compare")}
              onDelete={() => openClientDialog(c, "delete")}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border divide-y">
          {displayed.map((c) => (
            <ClientListRow
              key={c.id}
              client={c}
              canDelete={canDelete}
              isDeleting={del.isPending}
              onEditInfo={() => openClientDialog(c, "info")}
              onEditIndustries={() => openClientDialog(c, "industries")}
              onCompare={() => openClientDialog(c, "compare")}
              onDelete={() => openClientDialog(c, "delete")}
            />
          ))}
        </div>
      )}

      {needsPagination ? (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, paginatedBase.length)} of {paginatedBase.length}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground px-2">
              {page + 1} / {pageCount}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pageCount - 1}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}

      <CreateClientDialog open={createOpen} onOpenChange={setCreateOpen} />

      {selectedClient ? (
        <>
          <AlertDialog
            open={openDialog === "delete"}
            onOpenChange={closeDialog}
            title={`Delete "${selectedClient.name}"?`}
            description="All reporting periods and notes for this client will be permanently removed. This can't be undone."
            confirmLabel="Delete client"
            onConfirm={() => void handleConfirmDelete()}
            isPending={del.isPending}
          />
          <ComparePickerDialog
            open={openDialog === "compare"}
            onOpenChange={closeDialog}
            currentSlug={selectedClient.slug}
            currentName={selectedClient.name}
          />
          <IndustriesDialog
            clientId={selectedClient.id}
            clientName={selectedClient.name}
            currentIndustries={selectedClient.industries}
            open={openDialog === "industries"}
            onOpenChange={closeDialog}
          />
          <ClientInfoDialog
            clientId={selectedClient.id}
            clientName={selectedClient.name}
            current={selectedClient}
            open={openDialog === "info"}
            onOpenChange={closeDialog}
          />
        </>
      ) : null}
    </div>
  );
}

function NoClientsEmptyState({ onCreate }: { onCreate: () => void }): React.JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center gap-3 p-12 text-center">
        <h3 className="text-lg font-semibold">No clients yet</h3>
        <p className="max-w-sm text-sm text-muted-foreground">
          Add your first client to get started. You'll be able to upload monthly CSVs and review
          dashboards once a client exists.
        </p>
        <Button onClick={onCreate} className="mt-2">
          <Plus className="h-4 w-4" />
          Create your first client
        </Button>
      </CardContent>
    </Card>
  );
}

export function HealthBadge({
  score,
  previousScore,
  latestPeriodAt,
  appAdoptionRate,
}: {
  score: number | null;
  previousScore?: number | null;
  latestPeriodAt?: string | null;
  // App-Only clients have no device/session data, so they can never get a
  // Health Score (see computeHealthScore) -- this is what they show in its
  // place, clearly labeled as a different metric rather than as "No score".
  appAdoptionRate?: number | null;
}): React.JSX.Element {
  const stale = isStale(latestPeriodAt);
  const staleDays = stale && latestPeriodAt ? daysSinceLatestPeriod(latestPeriodAt) : 0;

  const trendDelta = score !== null && previousScore != null ? score - previousScore : null;

  const pill =
    score === null && appAdoptionRate != null ? (
      <span
        className={cn(
          "num inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium tabular-nums",
          BAND_META.healthy.bgClass,
          BAND_META.healthy.textClass,
        )}
      >
        <span className={cn("h-1.5 w-1.5 rounded-full", BAND_META.healthy.dotClass)} />
        {Math.round(appAdoptionRate)}%
        <span className="font-normal opacity-80">App Adoption</span>
      </span>
    ) : score === null ? (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
        No score
      </span>
    ) : (
      (() => {
        const meta = BAND_META[bandFor(score)];
        return (
          <span
            className={cn(
              "num inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium tabular-nums",
              meta.bgClass,
              meta.textClass,
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", meta.dotClass)} />
            {score}
            <span className="font-normal opacity-80">{meta.label}</span>
          </span>
        );
      })()
    );

  if (trendDelta === null && !stale) return pill;

  return (
    <div className="inline-flex items-center gap-1.5">
      {pill}
      {trendDelta !== null && trendDelta !== 0 ? (
        trendDelta > 0 ? (
          <ArrowUp className="h-3.5 w-3.5 flex-shrink-0 text-pill-green" aria-hidden="true" />
        ) : (
          <ArrowDown className="h-3.5 w-3.5 flex-shrink-0 text-pill-red" aria-hidden="true" />
        )
      ) : null}
      {stale ? (
        <span
          role="img"
          aria-label={`Data is ${staleDays} days old`}
          title={`Data is ${staleDays} days old`}
          className="flex-shrink-0"
        >
          <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );
}

const SHORT_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatContractDate(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  const month = SHORT_MONTHS[parseInt(m ?? "1", 10) - 1] ?? "";
  return `${parseInt(d ?? "1", 10)} ${month}`;
}

function contractDateStatus(dateStr: string): "expired" | "soon" | "ok" {
  const end = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((end.getTime() - now.getTime()) / 86_400_000);
  if (diffDays < 0) return "expired";
  if (diffDays <= 60) return "soon";
  return "ok";
}

interface ClientRowActions {
  canDelete: boolean;
  isDeleting: boolean;
  onEditInfo: () => void;
  onEditIndustries: () => void;
  onCompare: () => void;
  onDelete: () => void;
}

// Icon-only action buttons shared by the card and list-row layouts below.
// Both are plain siblings of the name link now (not nested inside it), so
// they need no preventDefault/stopPropagation -- a click on a sibling never
// triggers the link (UI-15).
function ClientRowActionButtons({
  client: c,
  canDelete,
  isDeleting,
  onEditInfo,
  onEditIndustries,
  onCompare,
  onDelete,
}: ClientRowActions & { client: client.ClientListItem }): React.JSX.Element {
  return (
    <div className="relative z-10 flex items-center gap-0.5">
      <Button variant="ghost" size="icon" onClick={onEditInfo} aria-label={`Edit info for ${c.name}`}>
        <Info className="h-4 w-4 text-muted-foreground" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={onEditIndustries}
        aria-label={`Edit industries for ${c.name}`}
      >
        <Tags className="h-4 w-4 text-muted-foreground" />
      </Button>
      <Button variant="ghost" size="icon" onClick={onCompare} aria-label={`Compare ${c.name}`}>
        <GitCompareArrows className="h-4 w-4 text-muted-foreground" />
      </Button>
      {canDelete ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          disabled={isDeleting}
          aria-label={`Delete ${c.name}`}
          className="hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-4 w-4 text-muted-foreground" />
        </Button>
      ) : null}
    </div>
  );
}

// Tinted pills, same palette as the Industry Insights page (UI-consistency;
// see IndustryInsightsPage's IndustryCard for the canonical colour source)
// but translucent rather than solid fill -- same treatment as the health
// score chips (BAND_META), so a card with several tags doesn't read as
// busier than the health badge next to it.
function IndustryTags({ industries }: { industries: string[] }): React.JSX.Element | null {
  const { theme } = useTheme();
  if (industries.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {industries.map((industry) => {
        const color = industryColor(industry);
        return (
          <span
            key={industry}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{
              background: pillTint(color, theme === "dark" ? 0.2 : 0.12),
              color: readablePillText(color, theme === "dark"),
            }}
          >
            {industry}
          </span>
        );
      })}
    </div>
  );
}

function ClientCard({
  client: c,
  ...actions
}: { client: client.ClientListItem } & ClientRowActions): React.JSX.Element {
  const contractStatus = c.contractEndDate ? contractDateStatus(c.contractEndDate) : null;

  return (
    <Card className="relative h-full">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="truncate">
              <Link to={`/clients/${c.slug}`} className="after:absolute after:inset-0 hover:underline">
                {c.name}
              </Link>
            </CardTitle>
            <IndustryTags industries={c.industries} />
          </div>
          <ClientRowActionButtons client={c} {...actions} />
        </div>
      </CardHeader>
      <CardContent>
        <HealthBadge
          score={c.healthScore}
          previousScore={c.previousHealthScore}
          latestPeriodAt={c.latestPeriodAt}
          appAdoptionRate={c.appAdoptionRate}
        />
        <div className="mt-3 flex items-center gap-4 text-sm">
          <div>
            <span className="font-medium">{c.periodCount}</span>{" "}
            <span className="text-muted-foreground">
              {c.periodCount === 1 ? "period" : "periods"}
            </span>
          </div>
        </div>
        {c.contractEndDate ? (
          <p
            className={`mt-2 text-xs font-medium ${
              contractStatus === "expired"
                ? "text-destructive"
                : contractStatus === "soon"
                  ? "text-pill-orange"
                  : "text-muted-foreground"
            }`}
          >
            {contractStatus === "expired" ? "Contract expired" : "Renews"}{" "}
            {formatContractDate(c.contractEndDate)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ClientListRow({
  client: c,
  ...actions
}: { client: client.ClientListItem } & ClientRowActions): React.JSX.Element {
  const contractStatus = c.contractEndDate ? contractDateStatus(c.contractEndDate) : null;

  return (
    <div className="relative flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors">
      <div className="min-w-0 flex-1 flex items-center gap-6">
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate">
            <Link to={`/clients/${c.slug}`} className="after:absolute after:inset-0 hover:underline">
              {c.name}
            </Link>
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground shrink-0">
          <span>
            <span className="font-medium text-foreground">{c.periodCount}</span>{" "}
            {c.periodCount === 1 ? "period" : "periods"}
          </span>
          {c.industries.length > 0 ? (
            <span>{c.industries.join(", ")}</span>
          ) : (
            <span>No industries</span>
          )}
          {c.contractEndDate && contractStatus !== "ok" ? (
            <span
              className={`text-xs font-medium ${
                contractStatus === "expired" ? "text-destructive" : "text-pill-orange"
              }`}
            >
              {contractStatus === "expired" ? "Contract expired" : "Renews"}{" "}
              {formatContractDate(c.contractEndDate)}
            </span>
          ) : null}
        </div>
      </div>
      <div className="shrink-0">
        <HealthBadge
          score={c.healthScore}
          previousScore={c.previousHealthScore}
          latestPeriodAt={c.latestPeriodAt}
          appAdoptionRate={c.appAdoptionRate}
        />
      </div>
      <div className="shrink-0">
        <ClientRowActionButtons client={c} {...actions} />
      </div>
    </div>
  );
}
